//src/components/header/shared/MobileHeaderShell.tsx

'use client';

import React, { useEffect, useRef, useState, type ComponentProps, type ElementType } from 'react';

type ShellBaseProps = {
  /** 外殼語意標籤（預設 'header'） */
  as?: ElementType;
  /** 額外 class（會加在最外層） */
  className?: string;

  /** sticky 區塊樣式（例：style.header） */
  stickyClassName?: string;
  /** 隱藏時的樣式（例：style.hidden） */
  hiddenClassName?: string;

  /** 向下滑隱藏、向上顯示（預設 true） */
  hideOnScroll?: boolean;
  /** 觸發隱藏的門檻高度（px，預設 300） */
  threshold?: number;
};

/** 泛型外殼：把任意 Content 元件 + 其 props 透進來 */
export type MobileHeaderShellProps<C extends ElementType> = ShellBaseProps & {
  Content: C;
  contentProps?: ComponentProps<C>;
};

const MobileHeaderShell = <C extends ElementType>({
  as: Tag = 'header',
  className,
  stickyClassName,
  hiddenClassName,
  hideOnScroll = true,
  threshold = 300,
  Content,
  contentProps,
}: MobileHeaderShellProps<C>) => {
  const [show, setShow] = useState(true);
  const lastYRef = useRef(0);

  useEffect(() => {
    if (!hideOnScroll) {
      setShow(true);
      return;
    }
    const onScroll = () => {
      const y = window.scrollY;
      const shouldHide = y > lastYRef.current && y > threshold;
      setShow(!shouldHide);
      lastYRef.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hideOnScroll, threshold]);

  const shellCls = [stickyClassName, !show && hiddenClassName, className].filter(Boolean).join(' ');

  return (
    <Tag className={shellCls}>
      <div className="container-fluid">
        {/* 內容由分頁自己提供的 HeaderContent 負責 */}
        <Content {...(contentProps as ComponentProps<C>)} />
      </div>
    </Tag>
  );
};

export default MobileHeaderShell;
